import { postGenerateAd } from '../../lib/generateAdRequest';
import { buildTestBody } from './buildTestBody';
import { configBaseId, poseDeCelda } from './celdas';
import type { Celda, TestSet } from './tipos';

/**
 * Dispatcher de una corrida: dispara las celdas PENDIENTES con un pool de
 * concurrencia acotada. No espera la imagen — un 202 alcanza (la completitud la
 * decide el poll de celdas.ts sobre imagenes_generadas). Reanudable por diseño:
 * las celdas que no están en 'pendiente' no se tocan, así que reabrir una
 * corrida a medio disparar simplemente continúa donde quedó.
 */
export async function ejecutarCorrida(opts: {
  set: TestSet;
  celdas: Celda[];
  /** Notificación de cada cambio de estado — la UI persiste vía guardarCeldas. */
  onCeldas: (celdas: Celda[]) => void;
  /** Inyectable para test; default: postGenerateAd real. */
  post?: (body: Record<string, unknown>) => Promise<unknown>;
  concurrencia?: number;
  ahora?: () => number;
}): Promise<Celda[]> {
  const post = opts.post ?? ((body: Record<string, unknown>) => postGenerateAd(body));
  const ahora = opts.ahora ?? (() => Date.now());
  const concurrencia = opts.concurrencia ?? 3;
  const configPorId = new Map(opts.set.configs.map((c) => [c.config_id, c]));
  // El config_id de la celda puede venir EXPANDIDO por pose (`base::p:<id>`):
  // la config se resuelve por el id base y la pose por el sufijo.

  const celdas = [...opts.celdas];
  const indicesPendientes = celdas
    .map((c, i) => (c.estado === 'pendiente' ? i : -1))
    .filter((i) => i >= 0);

  const actualizar = (i: number, cambios: Partial<Celda>) => {
    celdas[i] = { ...celdas[i]!, ...cambios };
    opts.onCeldas([...celdas]);
  };

  let cursor = 0;
  const worker = async () => {
    while (cursor < indicesPendientes.length) {
      const i = indicesPendientes[cursor]!;
      cursor += 1;
      const celda = celdas[i]!;
      const config = configPorId.get(configBaseId(celda.config_id));
      if (!config) {
        // La config ya no existe en el set (celda huérfana): no hay body posible.
        actualizar(i, { estado: 'fallida' });
        continue;
      }
      // 'disparada' se marca ANTES de esperar el post: si la pestaña muere en el
      // medio, el poll igual va a encontrar la fila (el request ya salió o no —
      // y si no salió, el timeout la pasa a sin_resultado y se reintenta).
      actualizar(i, { estado: 'disparada', disparada_en: ahora() });
      try {
        await post(
          buildTestBody(
            config.payload,
            celda.variante_id,
            celda.execution_id,
            poseDeCelda(opts.set, celda.config_id),
          ),
        );
      } catch {
        actualizar(i, { estado: 'fallida' });
      }
    }
  };

  await Promise.all(Array.from({ length: Math.min(concurrencia, indicesPendientes.length) }, worker));
  return celdas;
}
